import { injectable } from 'tsyringe';

import type {
  EmbeddedSignupLauncher,
  EmbeddedSignupResult,
} from '@/modules/settings/domain/channels/EmbeddedSignupLauncher';
import type { WhatsAppSignupConfig } from '@/modules/settings/domain/channels/WhatsAppSignupConfig';
import { FacebookSdkLoader } from '@/modules/settings/adapters/channels/FacebookSdkLoader';
import type { FacebookSdk } from '@/modules/settings/adapters/channels/FacebookSdkLoader';

interface SessionInfoMessage {
  type: 'WA_EMBEDDED_SIGNUP';
  // FINISH has variants: FINISH_ONLY_WABA, FINISH_OBO_MIGRATION, FINISH_GRANT_ONLY_API_ACCESS...
  event: string;
  data: {
    waba_id?: string;
    current_step?: string;
    error_message?: string;
  };
}

// The signup UI runs on business.facebook.com while the SDK bridge posts from www.
const isFacebookOrigin = (origin: string): boolean => {
  try {
    const { protocol, hostname } = new URL(origin);

    return (
      protocol === 'https:' && (hostname === 'facebook.com' || hostname.endsWith('.facebook.com'))
    );
  } catch {
    return false;
  }
};

// The dialog callback can beat a FINISH event that is already in flight.
const LATE_EVENT_GRACE_MS = 1000;

const isSessionInfoMessage = (value: unknown): value is SessionInfoMessage => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const { type, event, data } = value as Partial<SessionInfoMessage>;

  return (
    type === 'WA_EMBEDDED_SIGNUP' &&
    typeof event === 'string' &&
    typeof data === 'object' &&
    data !== null
  );
};

@injectable()
export class FacebookEmbeddedSignupLauncher implements EmbeddedSignupLauncher {
  constructor(private readonly sdkLoader: FacebookSdkLoader) {}

  async preload(config: WhatsAppSignupConfig): Promise<void> {
    await this.sdkLoader.load(config.metaAppId);
  }

  launch(config: WhatsAppSignupConfig): Promise<EmbeddedSignupResult | null> {
    const loaded = this.sdkLoader.get();

    return loaded
      ? this.run(loaded, config)
      : this.sdkLoader.load(config.metaAppId).then(sdk => this.run(sdk, config));
  }

  private run(
    sdk: FacebookSdk,
    config: WhatsAppSignupConfig,
  ): Promise<EmbeddedSignupResult | null> {
    return new Promise((resolve, reject) => {
      let settled = false;

      const settle = (outcome: () => void) => {
        settled = true;
        window.removeEventListener('message', onMessage);
        outcome();
      };

      const onMessage = (event: MessageEvent) => {
        if (!isFacebookOrigin(event.origin)) {
          return;
        }
        const payload = this.parse(event.data);

        if (!payload) {
          return;
        }

        if (payload.event.startsWith('FINISH')) {
          const wabaId = payload.data.waba_id;

          settle(() =>
            wabaId
              ? resolve({ wabaId })
              : reject(new Error('Embedded Signup finished without a WhatsApp account')),
          );

          return;
        }

        if (payload.event === 'ERROR') {
          settle(() => reject(new Error(payload.data.error_message ?? 'Embedded Signup failed')));

          return;
        }

        // Only CANCEL ends the flow; any other event is an intermediate step of the popup.
        if (payload.event === 'CANCEL') {
          settle(() => resolve(null));
        }
      };

      window.addEventListener('message', onMessage);

      this.startLogin(
        sdk,
        config,
        () =>
          window.setTimeout(() => {
            if (!settled) {
              settle(() => resolve(null));
            }
          }, LATE_EVENT_GRACE_MS),
        error => settle(() => reject(error)),
      );
    });
  }

  private startLogin(
    sdk: FacebookSdk,
    config: WhatsAppSignupConfig,
    onDialogClosed: () => void,
    onFailure: (error: unknown) => void,
  ): void {
    try {
      sdk.login(onDialogClosed, {
        config_id: config.metaConfigId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {
            solutionID: config.solutionId,
          },
        },
      });
    } catch (error) {
      onFailure(error);
    }
  }

  private parse(data: unknown): SessionInfoMessage | null {
    if (isSessionInfoMessage(data)) {
      return data;
    }

    if (typeof data !== 'string') {
      return null;
    }

    try {
      const parsed: unknown = JSON.parse(data);

      return isSessionInfoMessage(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
}
